"use client";

import { SchoolData } from "@nimble/candidate-api/types";
import { RootErrorAlert } from "@nimble/react-requests/components/shared/root-error-alert";
import { InvitationItem } from "@nimble/recommendation-client/types";
import { formatRelativeDays } from "@nimble/ts-formatters/format-date";
import * as Sentry from "@sentry/nextjs";
import { Mail, MessageSquareText, UserCheck } from "lucide-react";
import { type JSX, type ReactNode, useEffect } from "react";

import { PrincipalCardAvatar } from "@/app/(discover)/candidate/invites/components/principal-card";
import {
  formatMatchedGradesDisplay,
  normalizeBrToNewline,
} from "@/app/(discover)/candidate/invites/lib/invites-page.utils";
import MatchTag, {
  MatchTagSize,
} from "@/components/shared/match-tag/match-tag";
import { SchoolDetailsHeader } from "@/components/shared/school";
import { SchoolLinkTarget } from "@/components/shared/school/school-details/school-name-link";
import { Badge } from "@/components/ui/badge";
import { formatPrincipalName } from "@/lib/helpers/format-principal-name";
import { formatSubjectLabels } from "@/lib/serializers/invites.serializer";
import statesConfig from "@/lib/states-config";

interface RecommendationCardProps {
  invitation: InvitationItem;
  schoolData: SchoolData;
  bottomContent: ReactNode;
}

type ValidationResult =
  | { isValid: true }
  | { isValid: false; error: string; details: Record<string, unknown> };

function validateInvitationData(
  invitation: InvitationItem,
  schoolData: SchoolData,
): ValidationResult {
  if (!invitation.school_nces_id) {
    return {
      isValid: false,
      error: "Missing school_nces_id",
      details: { invitation },
    };
  }

  if (!schoolData.name) {
    return {
      isValid: false,
      error: "Missing school name",
      details: { schoolData },
    };
  }

  const hasMessage = !!invitation.school_message;
  const hasMatches = hasMatchedCriteria(invitation);
  if (!hasMessage && !hasMatches) {
    return {
      isValid: false,
      error: "No message and no matched criteria",
      details: { invitation },
    };
  }

  return { isValid: true };
}

function buildReachedOutLabel(invitation: InvitationItem): string {
  const relative = formatRelativeDays(invitation.school_message_at, Date.now());
  return `Reached out to you ${relative ? `· ${relative}` : ""}`;
}

function PrincipalHeader({
  invitation,
  schoolData,
}: {
  invitation: InvitationItem;
  schoolData: SchoolData;
}): JSX.Element {
  const hasName = Boolean(
    schoolData.principal?.first_name || schoolData.principal?.last_name,
  );
  const principalName = formatPrincipalName(
    schoolData.principal?.first_name,
    schoolData.principal?.last_name,
  );

  return (
    <div className="flex items-start justify-between gap-3 w-full">
      <div className="flex items-center gap-3">
        <PrincipalCardAvatar
          schoolData={schoolData}
          className="bg-brand-purple-light text-brand-purple"
        />
        <div className="flex flex-col">
          <span className="font-bold text-sm text-foreground">
            {hasName ? `Principal ${principalName}` : "A principal"}
          </span>
          <span className="text-xs text-ink-subtle">
            {buildReachedOutLabel(invitation)}
          </span>
        </div>
      </div>
      <Badge className="flex-shrink-0 gap-1 bg-brand-purple-light text-brand-purple py-1">
        <Mail className="h-3.5 w-3.5" aria-hidden="true" />
        Invitation
      </Badge>
    </div>
  );
}

function SchoolInfoSection({
  invitation,
  schoolData,
}: {
  invitation: InvitationItem;
  schoolData: SchoolData;
}): JSX.Element {
  const mergedSchoolData: SchoolData = {
    ...schoolData,
    distance_from_search_location_miles: invitation.distance_from_school || 0,
  };

  return (
    <SchoolDetailsHeader
      schoolData={mergedSchoolData}
      schoolLinkTarget={SchoolLinkTarget.NewTab}
      stateConfig={statesConfig.all}
    />
  );
}

function hasMatchedCriteria(invitation: InvitationItem): boolean {
  return (
    (invitation.matched_subjects && invitation.matched_subjects.length > 0) ||
    (invitation.matched_grades && invitation.matched_grades.length > 0) ||
    invitation.distance_from_school != null
  );
}

function MatchedTagsList({
  invitation,
}: {
  invitation: InvitationItem;
}): JSX.Element | null {
  if (!hasMatchedCriteria(invitation)) return null;

  const matchedGradesDisplay = formatMatchedGradesDisplay(
    invitation.matched_grades,
  );

  return (
    <div className="flex flex-col gap-2 w-full">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
        What you matched on
      </span>
      <div className="flex flex-row flex-wrap items-center gap-1.5">
        {invitation.distance_from_school != null && (
          <MatchTag variant="green" size={MatchTagSize.Small}>
            {invitation.distance_from_school.toFixed(1)} miles away
          </MatchTag>
        )}
        {formatSubjectLabels(invitation.matched_subjects).map((subject) => (
          <MatchTag key={subject} variant="green" size={MatchTagSize.Small}>
            {subject}
          </MatchTag>
        ))}
        {matchedGradesDisplay && (
          <MatchTag variant="green" size={MatchTagSize.Small}>
            {matchedGradesDisplay}
          </MatchTag>
        )}
      </div>
    </div>
  );
}

function PrincipalMessageBox({
  principalFirstName,
  message,
}: {
  principalFirstName?: string | null;
  message: string;
}): JSX.Element {
  const label = principalFirstName
    ? `${principalFirstName}'s message`
    : "Their message";

  return (
    <div className="w-full bg-brand-purple-light border border-brand-purple/20 rounded-lg p-4">
      <div className="flex items-center gap-1.5 mb-2">
        <MessageSquareText
          className="h-4 w-4 text-brand-purple"
          aria-hidden="true"
        />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-purple">
          {label}
        </span>
      </div>
      <p className="text-sm text-ink-muted leading-relaxed whitespace-pre-line">
        {normalizeBrToNewline(message)}
      </p>
    </div>
  );
}

function PrincipalInvitationNotice({
  principalName,
}: {
  principalName: string;
}): JSX.Element {
  return (
    <div className="flex flex-col items-start gap-1 w-full">
      <div className="flex items-center gap-2">
        <UserCheck className="h-4 w-4 text-brand-purple" aria-hidden="true" />
        <h4 className="font-semibold text-sm leading-6 text-ink-dark">
          {principalName} invited you to connect
        </h4>
      </div>
      <p className="text-sm text-ink-muted leading-5">
        They&apos;d like to learn more about you.
      </p>
    </div>
  );
}

function InvitationCardError({
  invitationId,
  errorMessage,
  details,
}: {
  invitationId: string;
  errorMessage: string;
  details: Record<string, unknown>;
}): JSX.Element {
  useEffect(() => {
    Sentry.captureException(
      new Error(`Invitation card error: ${errorMessage}`),
      { extra: { invitationId, ...details } },
    );
  }, [invitationId, errorMessage, details]);

  return (
    <RootErrorAlert message="Something went wrong loading this invitation." />
  );
}

export function RecommendationCard({
  invitation,
  schoolData,
  bottomContent,
}: RecommendationCardProps): JSX.Element {
  const validation = validateInvitationData(invitation, schoolData);

  if (!validation.isValid) {
    return (
      <InvitationCardError
        invitationId={invitation.school_nces_id}
        errorMessage={validation.error}
        details={validation.details}
      />
    );
  }

  return (
    <div className="group flex w-full flex-col gap-4 rounded-lg border border-line-faint p-4 transition-all hover:border-border hover:shadow-sm">
      <PrincipalHeader invitation={invitation} schoolData={schoolData} />
      <SchoolInfoSection invitation={invitation} schoolData={schoolData} />
      {invitation.school_message ? (
        <PrincipalMessageBox
          principalFirstName={schoolData.principal?.first_name}
          message={invitation.school_message}
        />
      ) : (
        <PrincipalInvitationNotice
          principalName={formatPrincipalName(
            schoolData.principal?.first_name,
            schoolData.principal?.last_name,
          )}
        />
      )}
      <MatchedTagsList invitation={invitation} />
      {bottomContent}
    </div>
  );
}
